/**
 * Formateo de la dirección de entrega a texto legible (una sola verdad).
 *
 * Tres superficies necesitan la misma cadena y antes cada una la armaba a mano:
 * el paso de dirección del checkout (para que el cliente confirme lo que eligió),
 * el `delivery_address` que se persiste en la orden (lo lee el courier y la
 * comanda), y el snapshot del intent de reconciliación. Divergir ahí significa
 * que el repartidor ve una dirección distinta a la que el cliente confirmó.
 *
 * Estructural a propósito: acepta cualquier objeto con estos campos (p. ej.
 * `DropoffAddress` del microsite o `Address` de lib/delivery/provider) sin que
 * `lib/` tenga que importar tipos de `components/`.
 */
export interface DropoffLike {
  street: string
  city: string
  state: string
  zip?: string
  /** Apto / suite / instrucciones de entrega. No viaja a la cotización. */
  unit?: string
}

function clean(v: string | undefined | null): string {
  return (v ?? "").trim()
}

/**
 * "123 Main St, Austin, TX 78701" — SIN apto/suite.
 *
 * Es la línea que identifica la ubicación cotizada: el `unit` no cambia el precio
 * ni la zona, así que se omite acá para que el cliente compare exactamente lo que
 * se le cotizó. Los campos vacíos se saltan (nunca ", ," ni un ZIP colgando).
 */
export function formatDropoffLine(a: DropoffLike): string {
  const cityState = [clean(a.city), [clean(a.state), clean(a.zip)].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
  return [clean(a.street), cityState].filter(Boolean).join(", ")
}

/**
 * "123 Main St, Apt 4B, Austin, TX 78701" — CON apto/suite.
 *
 * Ésta es la que se guarda en `orders.delivery_address`: el courier necesita el
 * apto o no entrega. Sin `unit` es idéntica a `formatDropoffLine`.
 */
export function formatDropoffFull(a: DropoffLike): string {
  const unit = clean(a.unit)
  if (!unit) return formatDropoffLine(a)
  const cityState = [clean(a.city), [clean(a.state), clean(a.zip)].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
  return [clean(a.street), unit, cityState].filter(Boolean).join(", ")
}

/** ETA del courier en minutos (redondeado hacia arriba), o null si el proveedor no la dio. */
export function etaMinutes(etaDropoffSeconds: number | undefined | null): number | null {
  if (etaDropoffSeconds == null || !Number.isFinite(etaDropoffSeconds) || etaDropoffSeconds <= 0) {
    return null
  }
  return Math.max(1, Math.ceil(etaDropoffSeconds / 60))
}
